import { asSet, filterTree, flattenTree, getAncestorIds, getChildrenIds } from 'kineticUtils';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TreeMenuItem, UseTreeMenuOptions } from './types';

/**
 * Hook for controlling a Tree Menu.
 *
 * if you don't need access to the expanded or selected state, you don't need to use this.
 *
 * @returns [TreeMenuProps, ExpandedUseState, SelectedUseState]
 */
export const useTreeMenu = (options: UseTreeMenuOptions = {}) => {
  const { onExpandedChange, onSelectedChange, unselectOnToggle } = options;
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | string[]>('');

  // React.ChangeEvent<{}> is what TreeView requires
  const onNodeToggle = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-types
    (event: React.ChangeEvent<{}>, nodeId: string) => {
      setExpanded((p) => [...p, nodeId]);
      onExpandedChange?.(event, [...expanded, nodeId]);
    },
    [onExpandedChange, expanded]
  );

  const onNodeSelect = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-types
    (event: React.ChangeEvent<{}>, nodeId: string) => {
      if (unselectOnToggle && nodeId === selected) {
        setSelected('');
        onSelectedChange?.(event, '');
      } else {
        setSelected(nodeId);
        onSelectedChange?.(event, nodeId);
      }
    },
    [onSelectedChange, setSelected, selected, unselectOnToggle]
  );
  return [
    { onNodeToggle, onNodeSelect, expanded, selected },
    {
      expand: [expanded, setExpanded],
      select: [selected, setSelected],
    },
  ] as const;
};

/**
 * Hook to watch the route and force selection/expansion of a TreeMenu.
 */
export const useTreeMenuRouteSelection = (
  menu: TreeMenuItem[],
  treeMenuOptions: UseTreeMenuOptions = {}
) => {
  const { pathname, hash } = useLocation();

  const matchHash = useCallback(
    (menuItem: TreeMenuItem) => {
      const { to = '' } = menuItem;
      const hashPosition = to.indexOf('#');
      const hasHash = hashPosition > -1;
      const hashMatches = hasHash ? to.substr(hashPosition) === decodeURIComponent(hash) : false;

      return hashMatches;
    },
    [hash]
  );

  const matchPath = useCallback(
    (menuItem: TreeMenuItem) => {
      const { to = '' } = menuItem;
      const hashPosition = to.indexOf('#');
      const hasHash = hashPosition > -1;

      const hashMatches = hasHash ? matchHash(menuItem) : true;
      const pathMatches = hasHash ? to.substr(0, hashPosition) === pathname : to === pathname;

      return hashMatches && pathMatches;
    },
    [matchHash, pathname]
  );

  const isMatch = useCallback(
    (item: TreeMenuItem) => {
      const { to = '' } = item;
      let match = false;

      if (to.indexOf('#') > -1) {
        match = matchHash(item);
      } else {
        match = matchPath(item);
      }
      return match;
    },
    [matchHash, matchPath]
  );

  const trackMatches = useCallback(
    (item: TreeMenuItem, allMatches: string[], matchParents: string[], allParents: string[]) => {
      const parents: string[] = allParents;
      if (isMatch(item)) {
        allMatches.push(item.id);
        matchParents.push(...allParents);
      }
      parents.push(item.id);
      if (item.children) {
        item.children.forEach((item) => trackMatches(item, allMatches, matchParents, parents));
      }
    },
    [isMatch]
  );

  const { matches, parents } = useMemo(() => {
    const allMatches: string[] = [];
    const matchParents: string[] = [];
    menu.forEach((item) => {
      trackMatches(item, allMatches, matchParents, []);
    });
    return {
      matches: allMatches,
      parents: new Set(matchParents),
    };
  }, [menu, trackMatches]);

  const [
    props,
    {
      expand: [, setExpanded],
      select: [, setSelected],
    },
  ] = useTreeMenu(treeMenuOptions);

  useEffect(() => {
    setExpanded((prev) => asSet([...prev, ...Array.from(parents)]));
    setSelected(asSet(matches));
  }, [matches, parents, setExpanded, setSelected]);

  return props;
};

/**
 * Find a menu item by id in a TreeMenu.
 * @param menu  The menu to search
 * @param id  The id to search for
 * @returns  The menu item or null if not found
 */
export const findMenuItem = (menu: TreeMenuItem[], id: string): TreeMenuItem | null => {
  for (let i = 0; i < menu.length; i++) {
    const item = menu[i];
    if (item.id === id) {
      return item;
    } else if (item.children) {
      const child = findMenuItem(item.children, id);
      if (child) {
        return child;
      }
    }
  }
  return null;
};

/**
 * Hook to use a string value to search TreeMenu labels
 *
 * Expand all parents of exact match.
 * Hide items that aren't ancestor or descendant of exact match.
 */
export const useTreeMenuSearch = (menu: TreeMenuItem[]) => {
  const [searchValue, setSearchValue] = useState('');

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const resetSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const flatMenu = useMemo(() => flattenTree<TreeMenuItem>(menu), [menu]);
  const matchingItems = useMemo(() => {
    if (!searchValue || searchValue.trim().length === 0) {
      return [];
    }
    return flatMenu.filter((menuItem) =>
      menuItem.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [flatMenu, searchValue]);

  const matchingIds = useMemo(() => matchingItems.map((item) => item.id).sort(), [matchingItems]);

  const visibleParentIds = useMemo(
    () => asSet(matchingItems.map((item) => getAncestorIds<TreeMenuItem>(item, flatMenu)).flat()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchingItems]
  );
  const visibleChildrenIds = useMemo(
    () => asSet(matchingItems.map((item) => getChildrenIds<TreeMenuItem>(item)).flat()),
    [matchingItems]
  );

  const trimmedMenu = useMemo(() => {
    if (!searchValue.trim().length) {
      return menu;
    }
    const filtered = filterTree<TreeMenuItem>(menu, (item) => {
      return (
        visibleParentIds.includes(item.id) ||
        visibleChildrenIds.includes(item.id) ||
        matchingIds.includes(item.id)
      );
    });

    return filtered;
  }, [matchingIds, menu, searchValue, visibleChildrenIds, visibleParentIds]);

  return {
    resetSearch,
    searchProps: {
      onChange,
    },
    searchValue,
    treeMenuProps: {
      menu: trimmedMenu as TreeMenuItem[],
      expanded: visibleParentIds,
    },
  };
};
